import { useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useApi } from '../api/context';
import type { SecretMetadata, SettingDelivery, SettingLocation } from '../api/types';
import { useToast } from '../components/toast';
import { ErrorMessage } from '../components/ui';
import { formatBytes } from '../lib/format';
import { MAX_SECRET_VALUE_BYTES, utf8Length } from '../lib/secrets';
import { strings } from '../strings';
import { deliveryEntry, joinApps, locationEntry } from './registry';

const s = strings.variables.setValue;

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

/**
 * Set value (spec 6.10.11): a popover anchored to the row, write-only. The size counter, the "now" checkbox
 * and the pickup sentence come from the location and delivery registries.
 */
export function SetValuePopover({
  deploymentId,
  secret,
  location,
  delivery,
  anchor,
  onClose,
  runActive = false,
  deploymentName = '',
}: {
  deploymentId: string;
  secret: SecretMetadata;
  location: SettingLocation | null;
  delivery: SettingDelivery | null;
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Feature spec 15: a run of the deployment is active (the rollout checkbox is disabled). */
  runActive?: boolean;
  deploymentName?: string;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const panel = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const apps = [...new Set(secret.usedBy.map((u) => u.resourceId))];
  const entry = delivery ? deliveryEntry(delivery) : null;
  const now = delivery && entry ? entry.nowAction(apps, delivery) : null;
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [restart, setRestart] = useState(
    now ? now.default === 'on' || secret.onChange === 'restart' : secret.onChange === 'restart',
  );
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const max = location?.limits?.maxValueBytes ?? MAX_SECRET_VALUE_BYTES;
  const limitName = location ? locationEntry(location).limitLabel : strings.variables.limits.generic;
  const size = utf8Length(value);
  const tooLarge = size > max;
  // Feature spec 15 19.4: a purged AWS secret is created again with a new ID: no rollout, an apply is needed.
  const recreates =
    !!location?.limits?.recreateNeedsApply &&
    secret.drift.includes('deleted-outside') &&
    secret.recoverability === 'not-recoverable';
  const rolloutDisabled = apps.length === 0 || runActive;
  const problem = tooLarge ? s.tooLarge : submitted && value.length === 0 ? s.required : null;

  useLayoutEffect(() => {
    const place = () => {
      const width = Math.min(460, window.innerWidth - 16);
      const rect = anchor?.getBoundingClientRect();
      const height = panel.current?.offsetHeight ?? 320;
      if (!rect) {
        setPosition({ top: 80, left: Math.max(8, (window.innerWidth - width) / 2), width });
        return;
      }
      let top = rect.bottom + 4;
      if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4);
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      setPosition({ top, left, width });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  useEffect(() => {
    field.current?.focus();
    return () => {
      if (anchor?.isConnected) anchor.focus();
    };
  }, [anchor]);

  // A click outside closes it (like the menus); the value is dropped.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [onClose]);

  const submit = async () => {
    setSubmitted(true);
    if (value.length === 0 || tooLarge || busy) return;
    setBusy(true);
    setError(null);
    try {
      // The UI always sends `restart` explicitly (feature spec 15 19.4).
      const result = await api.setSecretValue(deploymentId, secret.name, {
        value,
        restart: recreates || rolloutDisabled ? false : restart,
      });
      setValue('');
      const restarted = result.restarts.filter((r) => r.succeeded).map((r) => r.resourceId);
      const failed = result.restarts.filter((r) => !r.succeeded);
      toast.show({
        tone: 'success',
        title: s.success(secret.name),
        detail: restarted.length
          ? (entry?.nowActionDoneText?.(restarted) ?? s.restarted(joinApps(restarted)))
          : undefined,
      });
      if (failed.length)
        toast.show({
          tone: 'warning',
          title: entry?.nowActionFailedText
            ? failed
                .map((r) => entry.nowActionFailedText!(r.resourceId, r.error ?? strings.common.unknownError))
                .join(' ')
            : s.restartFailed(joinApps(failed.map((r) => r.resourceId))),
          detail: entry?.nowActionFailedText
            ? undefined
            : failed.map((r) => `${r.resourceId}: ${r.error ?? strings.common.unknownError}`).join('; '),
        });
      for (const warning of result.warnings ?? [])
        toast.show({
          tone: 'warning',
          title: warning.message,
          action:
            warning.code === 'SECRET_RECREATED'
              ? {
                  label: strings.vaults.plan,
                  onSelect: () =>
                    void api
                      .createRun(deploymentId, { kind: 'plan' })
                      .then((run) => navigate(`/runs/${run.id}`))
                      .catch((caught: unknown) => toast.error(caught)),
                }
              : undefined,
        });
      void queryClient.invalidateQueries({ queryKey: ['deployment', deploymentId] });
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      void submit();
    } else if (event.key === 'Tab' && panel.current) {
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    // Keys typed here never reach the table's grid keys or the dock shortcuts' page handlers.
    event.stopPropagation();
  };

  const pickup = delivery && entry ? entry.pickupText(apps, delivery) : null;
  const restartLabel = now ? now.label : strings.setSecret.restart;
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${ids}-title`}
      className="popover var-popover"
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: position?.width ?? 460,
      }}
      onKeyDown={onKeyDown}
    >
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <KeyRound aria-hidden className="size-4 text-fg-muted" />
        <h3 id={`${ids}-title`} className="min-w-0 flex-1 truncate font-semibold">
          {s.title(secret.name)}
        </h3>
        {location && (
          <span className="text-[12px] text-fg-muted">{locationEntry(location).label(location)}</span>
        )}
      </div>
      <div className="space-y-2 px-3 py-2">
        {error !== null && <ErrorMessage error={error} />}
        <label htmlFor={`${ids}-value`} className="label">
          {s.value}
        </label>
        <textarea
          ref={field}
          id={`${ids}-value`}
          className={`input font-mono ${show ? '' : 'var-masked'}`}
          rows={3}
          value={value}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={problem ? true : undefined}
          aria-describedby={`${ids}-size ${ids}-help${problem ? ` ${ids}-error` : ''}`}
          onChange={(event) => setValue(event.target.value)}
        />
        <div className="flex items-center justify-between gap-2 text-[12px] text-fg-muted">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={show} onChange={(event) => setShow(event.target.checked)} />
            {s.show}
          </label>
          <span id={`${ids}-size`} className={tooLarge ? 'text-danger' : undefined}>
            {s.size(formatBytes(size), formatBytes(max), limitName)}
          </span>
        </div>
        {problem && (
          <p id={`${ids}-error`} className="field-error">
            {problem}
          </p>
        )}
        {recreates ? (
          <p className="help" data-testid="recreate-note">
            {strings.vaults.recreated(secret.name, joinApps(apps) || 'The app', deploymentName)}
          </p>
        ) : (
          <div>
            <label className="flex items-start gap-2 text-[13px] font-medium">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={restart && !runActive}
                disabled={rolloutDisabled}
                onChange={(event) => setRestart(event.target.checked)}
              />
              <span>
                {restartLabel} {now && <span className="font-normal">{s.disruption(now.disruption)}</span>}
              </span>
            </label>
            <p className="help ml-6" data-testid="rollout-help">
              {apps.length === 0
                ? s.noApps
                : runActive && now
                  ? strings.vaults.rolloutDisabledRun
                  : restart && secret.onChange === 'wait' && entry?.nowActionDefaultReason
                    ? entry.nowActionDefaultReason(secret.name)
                    : !restart && pickup
                      ? pickup
                      : null}
            </p>
          </div>
        )}
        {location?.limits?.writeAdvice && <p className="help">{location.limits.writeAdvice}</p>}
        <p id={`${ids}-help`} className="help">
          {s.help}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
        <span className="mr-auto text-[11px] text-fg-faint">{s.keys}</span>
        <button type="button" className="btn" onClick={onClose}>
          {s.cancel} <span className="kbd">Esc</span>
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
          {s.submit} <span className="kbd">Ctrl ↵</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}
